import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { TABLE_ACTIONS } from 'common/Table/constants';
import { StyledTable } from 'common/Table/StyledTable';
import PositionCell from 'components/AiCompetitiveIntelligence/AICICompanyCommunityCells/PositionCell';
import ScoreCell from 'components/AiCompetitiveIntelligence/AICICompanyCommunityCells/ScoreCell';
import AiCiTableWrapper from 'components/AiCompetitiveIntelligence/AiCiTableWrapper';
import DetailsButton from 'components/lib/DetailsButton';
import { MyCommunitiesRow } from 'types/recommendations';

import ClickableCellValue from './cells/ClickableCellValue';
import CommunityNameCell from './cells/CommunityNameCell';
import IncentivesCell from './cells/IncentivesCell';
import RateGapCell from './cells/RateGapCell';
import ReviewsCell from './cells/ReviewsCell';
import { MY_COMMUNITIES_COLUMN_WIDTH, MY_COMMUNITIES_ORDERING } from './constants';
import CompetitorIncentivesModal from './modals/CompetitorIncentivesModal';
import MarketRateModal from './modals/MarketRateModal';
import ReviewsModal from './modals/ReviewsModal';

const COMMUNITY_AI_CI_PATH = '/ai-competitive-intelligence/overall-market-position';

const TableWrapper = styled(AiCiTableWrapper)`
  margin-top: 0;

  &&& .ant-table-tbody > tr > td.ant-table-cell {
    padding-left: 8px;
    height: 1px;
  }

  &&& .ant-table-thead > tr > th {
    font-size: 12px;
    font-family: var(--font-bold);
    padding-left: 8px;
    padding-top: 20px;
    padding-bottom: 10px;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }
`;

const DetailsCellContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 9px;
`;

type OpenModal = { type: 'rate-gap' | 'reviews' | 'incentives'; row: MyCommunitiesRow };

type Props = {
  rows: MyCommunitiesRow[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  showPricingData: boolean;
  ordering: string | null;
  timePeriodDays: number;
  timePeriodLabel: string;
  onPageChange: (page: number) => void;
  onOrderingChange: (ordering: string | null) => void;
};

const toOrdering = (sorter: any): string | null => {
  const field = sorter?.columnKey;
  if (!field || !sorter?.order) {
    return null;
  }
  return sorter.order === 'descend' ? `-${field}` : field;
};

const sortOrderFor = (ordering: string | null, field: string) => {
  if (!ordering || ordering.replace(/^-/, '') !== field) {
    return null;
  }
  return ordering.startsWith('-') ? ('descend' as const) : ('ascend' as const);
};

const MyCommunitiesTable = ({
  rows,
  loading,
  page,
  pageSize,
  total,
  showPricingData,
  ordering,
  timePeriodDays,
  timePeriodLabel,
  onPageChange,
  onOrderingChange,
}: Props) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<OpenModal | null>(null);

  const openCommunityAICI = (communityId: number) => {
    navigate(`${COMMUNITY_AI_CI_PATH}?communityId=${communityId}`);
  };

  const openCommunityAIOverview = (communityId: number) => {
    navigate(`/community/${communityId}/ai-overview`);
  };

  const sortableColumn = (field: string) => ({
    sorter: true,
    sortOrder: sortOrderFor(ordering, field),
    showSorterTooltip: false,
  });

  const columns = [
    {
      title: 'COMMUNITY',
      dataIndex: 'community_name',
      key: MY_COMMUNITIES_ORDERING.COMMUNITY_NAME,
      width: MY_COMMUNITIES_COLUMN_WIDTH.COMMUNITY,
      ...sortableColumn(MY_COMMUNITIES_ORDERING.COMMUNITY_NAME),
      render: (name: string) => <CommunityNameCell name={name} />,
    },
    {
      title: 'SCORE',
      dataIndex: 'score',
      key: MY_COMMUNITIES_ORDERING.SCORE,
      width: MY_COMMUNITIES_COLUMN_WIDTH.SCORE,
      ...sortableColumn(MY_COMMUNITIES_ORDERING.SCORE),
      render: (score: number | null, row: MyCommunitiesRow) => (
        <ClickableCellValue onClick={() => openCommunityAICI(row.community_id)} withChevron>
          <ScoreCell score={score} averageScore={null} />
        </ClickableCellValue>
      ),
    },
    {
      title: 'POSITION VS COMP.',
      dataIndex: 'rank',
      key: MY_COMMUNITIES_ORDERING.RANK,
      width: MY_COMMUNITIES_COLUMN_WIDTH.RANK,
      ...sortableColumn(MY_COMMUNITIES_ORDERING.RANK),
      render: (rank: number | null, row: MyCommunitiesRow) => (
        <ClickableCellValue onClick={() => openCommunityAICI(row.community_id)} withChevron>
          <PositionCell rank={rank} performance={row.performance} competitorsCount={row.competitors_count ?? 0} />
        </ClickableCellValue>
      ),
    },
    ...(showPricingData
      ? [
          {
            title: 'LARGEST MIN RATE GAP',
            dataIndex: 'rate_gap',
            key: MY_COMMUNITIES_ORDERING.RATE_GAP,
            width: MY_COMMUNITIES_COLUMN_WIDTH.RATE_GAP,
            ...sortableColumn(MY_COMMUNITIES_ORDERING.RATE_GAP),
            render: (rateGap: MyCommunitiesRow['rate_gap'], row: MyCommunitiesRow) => (
              <RateGapCell rateGap={rateGap} onClick={() => setOpenModal({ type: 'rate-gap', row })} />
            ),
          },
        ]
      : []),
    {
      title: 'REVIEWS',
      dataIndex: 'reviews_average',
      key: MY_COMMUNITIES_ORDERING.REVIEWS_AVERAGE,
      width: MY_COMMUNITIES_COLUMN_WIDTH.REVIEWS,
      ...sortableColumn(MY_COMMUNITIES_ORDERING.REVIEWS_AVERAGE),
      render: (reviewsAverage: number | null, row: MyCommunitiesRow) => (
        <ReviewsCell
          reviewsAverage={reviewsAverage}
          recentReviewsCount={row.recent_reviews_count}
          positiveReviewsCount={row.positive_reviews_count}
          negativeReviewsCount={row.negative_reviews_count}
          onClick={() => setOpenModal({ type: 'reviews', row })}
        />
      ),
    },
    ...(showPricingData
      ? [
          {
            title: 'COMP. INCENTIVES',
            dataIndex: 'new_competitor_incentives_count',
            key: MY_COMMUNITIES_ORDERING.NEW_COMPETITOR_INCENTIVES,
            width: MY_COMMUNITIES_COLUMN_WIDTH.COMPETITOR_INCENTIVES,
            ...sortableColumn(MY_COMMUNITIES_ORDERING.NEW_COMPETITOR_INCENTIVES),
            render: (newCompetitorIncentivesCount: number, row: MyCommunitiesRow) => (
              <IncentivesCell
                newCompetitorIncentivesCount={newCompetitorIncentivesCount}
                onClick={() => setOpenModal({ type: 'incentives', row })}
              />
            ),
          },
        ]
      : []),
    {
      title: '',
      key: 'details',
      fixed: 'right' as const,
      width: MY_COMMUNITIES_COLUMN_WIDTH.DETAILS,
      render: (_: unknown, row: MyCommunitiesRow) => (
        <DetailsCellContainer className="actions-hover-reveal">
          <DetailsButton onClick={() => openCommunityAIOverview(row.community_id)} />
        </DetailsCellContainer>
      ),
    },
  ];
  const tableScrollWidth = columns.reduce((sum, column) => sum + (column.width ?? 0), 0);

  return (
    <TableWrapper>
      <StyledTable
        columns={columns as any}
        tableLayout="fixed"
        dataSource={rows}
        loading={loading}
        rowKey={((row: MyCommunitiesRow) => row.community_id) as any}
        onChange={
          ((pagination: any, _filters: unknown, sorter: any, extra: any) => {
            if (extra?.action === TABLE_ACTIONS.SORT) {
              onOrderingChange(toOrdering(sorter));
            }
            if (extra?.action === TABLE_ACTIONS.PAGINATE) {
              onPageChange(pagination.current);
            }
          }) as any
        }
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: page,
          pageSize,
          total,
          showTotal: (totalCount, range) => (
            <span>
              Showing {range[1] - range[0] + 1} of {totalCount} records
            </span>
          ),
        }}
        size="middle"
        bordered={false}
        scroll={{ x: tableScrollWidth }}
      />
      {openModal?.type === 'rate-gap' && (
        <MarketRateModal
          communityId={openModal.row.community_id}
          communityName={openModal.row.community_name}
          rateGap={openModal.row.rate_gap}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal?.type === 'reviews' && (
        <ReviewsModal
          communityId={openModal.row.community_id}
          communityName={openModal.row.community_name}
          timePeriodDays={timePeriodDays}
          timePeriodLabel={timePeriodLabel}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal?.type === 'incentives' && (
        <CompetitorIncentivesModal
          communityId={openModal.row.community_id}
          communityName={openModal.row.community_name}
          timePeriodDays={timePeriodDays}
          timePeriodLabel={timePeriodLabel}
          onClose={() => setOpenModal(null)}
        />
      )}
    </TableWrapper>
  );
};

export default MyCommunitiesTable;
